"use client";

import React from "react";

/**
 * "Signed in as Jess" — the account line inside the account menu on the PUBLIC
 * program pages (#4897). See PublicAccountMenu, which also shows the name on
 * its closed button.
 *
 * WHY IT EXISTS, AND WHY IT IS NOT DECORATION. The phone app can sign these
 * pages in from a link it opens in the system browser sheet (see
 * `utils/webview/exchangePublicPageHandoff.ts`). Anyone can mint such a link
 * for their OWN account and send it to someone else, and a signed-out browser
 * will take it. Naming the account is the only visible signal a parent has that
 * they are about to enter a child's name, birthdate and medical details into
 * somebody else's family — so it belongs on EVERY page that door is armed on.
 * The enrolment form is the page where those details are actually typed.
 *
 * A source guard in `__tests__/SignedInAsBadge.test.tsx` derives the set of
 * public program pages from the filesystem and fails if one shows a signed-in
 * header without naming the account.
 *
 * Falls back to the email when there is no first name, and renders NOTHING
 * when there is neither — an empty "Signed in as" would be worse than silence.
 */
export interface SignedInAsBadgeProps {
  user: { firstName?: string | null; email?: string | null } | null | undefined;
  /** Wrap a long value instead of truncating it — for a menu with room to spare. */
  wrap?: boolean;
}

export function SignedInAsBadge({ user, wrap = false }: SignedInAsBadgeProps) {
  const label = user?.firstName?.trim() || user?.email?.trim() || "";
  if (!label) return null;

  return (
    <span
      data-testid="signed-in-as"
      // Truncates by default so it cannot push anything off a phone; the
      // `title` keeps the full value reachable. `wrap` is for the account menu,
      // which has room to show a long email in full.
      className={`text-sm text-gray-600 dark:text-gray-300 ${
        wrap ? "block break-all" : "truncate max-w-[45vw] sm:max-w-xs"
      }`}
      title={label}
    >
      Signed in as {label}
    </span>
  );
}

export default SignedInAsBadge;
